package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initKotlinNativeFromScratchPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/KotlinNativeFromScratch.md", FrontMatterElement.Builder { addScalar("title", "Kotlin/Native in `FROM scratch`: 593 KB for a hello-world, 9.5 MB for a Ktor service"); addScalar("searchTitle", "Static Kotlin/Native binary in a scratch Docker image, measured"); addScalar("date", "2026-09-13"); addScalar("description", "A statically linked Kotlin/Native binary that starts in an empty image and still resolves hostnames, the exact flags that produce it on a stock 2.4.10, what it costs in size, RSS and cold start — and the four files a Ktor service has to put back, because glibc dlopens its character-set converters."); }))
}

@Page("/blog/kotlin-native-from-scratch")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun KotlinNativeFromScratchPage() {
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A Kotlin/Native hello-world, linked statically against the host's glibc: ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("1 393 592 bytes")
        }
        org.jetbrains.compose.web.dom.Text(", no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PT_INTERP")
        }
        org.jetbrains.compose.web.dom.Text(", no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("NEEDED")
        }
        org.jetbrains.compose.web.dom.Text(" entry at all. In a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FROM scratch")
        }
        org.jetbrains.compose.web.dom.Text(" image that is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("606 825 bytes to pull")
        }
        org.jetbrains.compose.web.dom.Text(", and inside it ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("getaddrinfo")
        }
        org.jetbrains.compose.web.dom.Text(" still resolves ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("example.com")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That first paragraph is a hello-world, and the number in the title is its image.")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Correction, 2026-09-14 — read this before the recipe.")
            }
            org.jetbrains.compose.web.dom.Text(" The first version of this article said a Ktor service runs in ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("FROM scratch")
            }
            org.jetbrains.compose.web.dom.Text(" on this recipe. It does not: it starts, migrates and answers ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("401")
            }
            org.jetbrains.compose.web.dom.Text(", then throws ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("Failed to open iconv for charset UTF-8 with error code 22")
            }
            org.jetbrains.compose.web.dom.Text(" the first time it renders a page. ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Ktor's charset layer on Kotlin/Native is glibc ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("iconv")
                }
                org.jetbrains.compose.web.dom.Text(", and glibc's ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("iconv")
                }
                org.jetbrains.compose.web.dom.Text(" has no built-in converters")
            }
            org.jetbrains.compose.web.dom.Text(" — every one of them, UTF-8 included, is a gconv module ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("dlopen")
            }
            org.jetbrains.compose.web.dom.Text("ed at run time, and an empty image has none.")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("The first version of ")
            org.jetbrains.compose.web.dom.Em {
                org.jetbrains.compose.web.dom.Text("this correction")
            }
            org.jetbrains.compose.web.dom.Text(" then overshot, and said a Ktor service therefore cannot have a small image at all. That was arithmetic on mismatched units — on-disk library sizes added to a compressed image size — and it was wrong. Measured properly, with the five files glibc actually opens copied in: ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("9 522 896 bytes to pull, against 15 542 820 for the same service on ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("distroless/cc")
                }
                org.jetbrains.compose.web.dom.Text(".")
            }
            org.jetbrains.compose.web.dom.Text(" Still 1.63× smaller, still worth doing, and it is no longer ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("FROM scratch")
            }
            org.jetbrains.compose.web.dom.Text(" in any honest sense — the image carries a dynamic loader and a shared libc so that ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("dlopen")
            }
            org.jetbrains.compose.web.dom.Text(" has something to open.")
        }
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("So: the hello-world numbers in the title and the first paragraph stand, a service needs section 5, and the measurements in section 4 are unaffected — they were taken on paths that work.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Section 4 is about that service, because a hello-world cannot tell you what static linking costs.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One word about the sizes before anything else, because it is easy to get wrong and I did. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker image inspect --format '{{.Size}}'")
        }
        org.jetbrains.compose.web.dom.Text(" reports the ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("compressed")
        }
        org.jetbrains.compose.web.dom.Text(" size — what a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("pull")
        }
        org.jetbrains.compose.web.dom.Text(" downloads, not the bytes on disk. Calibrated here on a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("scratch")
        }
        org.jetbrains.compose.web.dom.Text(" image holding 10 MB of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/dev/urandom")
        }
        org.jetbrains.compose.web.dom.Text(" (reports 10 005 026) against one holding 10 MB of zeros (reports 11 602).")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is true ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("with the containerd image store")
        }
        org.jetbrains.compose.web.dom.Text(", which is the default for fresh installations from Docker Engine 29.0; this box runs 29.1.3, and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("docker info")
        }
        org.jetbrains.compose.web.dom.Text(" says ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Storage Driver: overlayfs")
        }
        org.jetbrains.compose.web.dom.Text(" with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("driver-type: io.containerd.snapshotter.v1")
        }
        org.jetbrains.compose.web.dom.Text(". On the classic graphdriver the same field is the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("unpacked")
        }
        org.jetbrains.compose.web.dom.Text(" size on disk instead, so if your numbers come out several times larger than the ones here, check which store you are on before assuming anything about the binary. Every image figure below is a pull size and says so.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Everything here is one script: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/sborka/tree/main/docs/research/static-probe") {
            org.jetbrains.compose.web.dom.Text("sborka/docs/research/static-probe")
        }
        org.jetbrains.compose.web.dom.Text(". Kotlin 2.4.10, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linuxX64")
        }
        org.jetbrains.compose.web.dom.Text(", Ubuntu 24.04 with glibc 2.39.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("1-why-this-was-not-possible") }) {
        org.jetbrains.compose.web.dom.Text("1. Why this was not possible")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Three separate obstructions, each with its own ticket.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("One — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("platform.posix")
            }
            org.jetbrains.compose.web.dom.Text(" names libc's libraries in a klib manifest.")
        }
        org.jetbrains.compose.web.dom.Text(" Every Linux binary the compiler produces links against ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-lresolv -lm -lpthread -lutil -lcrypt -lrt")
        }
        org.jetbrains.compose.web.dom.Text(", because that list is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linkerOpts")
        }
        org.jetbrains.compose.web.dom.Text(" in the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("platform.posix")
        }
        org.jetbrains.compose.web.dom.Text(" klib's manifest. No ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xoverride-konan-properties")
        }
        org.jetbrains.compose.web.dom.Text(" value reaches it: that flag names ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("konan.properties")
        }
        org.jetbrains.compose.web.dom.Text(" keys, and no key names that list. On a modern distribution five of the six supply nothing — against a host glibc 2.39, only ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libm")
        }
        org.jetbrains.compose.web.dom.Text(" exports anything the binary imports (")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ceil")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("floor")
        }
        org.jetbrains.compose.web.dom.Text("), and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libcrypt")
        }
        org.jetbrains.compose.web.dom.Text(" exports nothing on any version. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libcrypt")
        }
        org.jetbrains.compose.web.dom.Text(" is simply the one that breaks first, because distributions dropped ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libcrypt.so.1")
        }
        org.jetbrains.compose.web.dom.Text(" soonest, which is why the issue is filed under its name: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-55643") {
            org.jetbrains.compose.web.dom.Text("KT-55643")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Two — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-dynamic-linker")
            }
            org.jetbrains.compose.web.dom.Text(" is emitted unconditionally.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-linker-option -static")
        }
        org.jetbrains.compose.web.dom.Text(" does not produce a static binary, because the link command undoes it twice, in flags emitted ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("after")
        }
        org.jetbrains.compose.web.dom.Text(" the user's own. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("GccBasedLinker.finalLinkCommands")
        }
        org.jetbrains.compose.web.dom.Text(" adds ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-dynamic-linker")
        }
        org.jetbrains.compose.web.dom.Text(" and its value with no condition, twenty-nine lines above the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("+linkerArgs")
        }
        org.jetbrains.compose.web.dom.Text(" that brings in your flags; and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linkerKonanFlags.linux_x64")
        }
        org.jetbrains.compose.web.dom.Text(" carries a hardcoded ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Bdynamic")
        }
        org.jetbrains.compose.web.dom.Text(" in the middle of its list, which switches the linker back to preferring shared libraries so that ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-lc")
        }
        org.jetbrains.compose.web.dom.Text(" resolves to a GNU ld script naming the shared ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libc.so.6")
        }
        org.jetbrains.compose.web.dom.Text(". The first you cannot reach from a build file at all; the second you can, once you know it is there. Filed as ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-89362") {
            org.jetbrains.compose.web.dom.Text("KT-89362")
        }
        org.jetbrains.compose.web.dom.Text(", with a three-line patch and its test in ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/JetBrains/kotlin/pull/8127") {
            org.jetbrains.compose.web.dom.Text("JetBrains/kotlin#8127")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Three — musl deadlocks in the runtime.")
        }
        org.jetbrains.compose.web.dom.Text(" The other way to a small image is musl, and it does not work. Building a musl sysroot from Alpine packages — including Alpine's own musl-built ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libstdc++.a")
        }
        org.jetbrains.compose.web.dom.Text(", which is what the runtime needs — produces a binary that links cleanly and then hangs ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("before its first ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("println")
            }
        }
        org.jetbrains.compose.web.dom.Text(": forty-one lines of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("strace")
        }
        org.jetbrains.compose.web.dom.Text(", no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("write(2)")
        }
        org.jetbrains.compose.web.dom.Text(" at all, three threads all asleep in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FUTEX_WAIT")
        }
        org.jetbrains.compose.web.dom.Text(", two of them named ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("Main GC thread")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("GC Timer thread")
        }
        org.jetbrains.compose.web.dom.Text(". That is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-85658") {
            org.jetbrains.compose.web.dom.Text("KT-85658")
        }
        org.jetbrains.compose.web.dom.Text(", a lost ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FUTEX_WAKE")
        }
        org.jetbrains.compose.web.dom.Text(" in GC thread coordination. With ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xbinary=gc=noop")
        }
        org.jetbrains.compose.web.dom.Text(" the hang goes away and is replaced by a segfault at address 0 ten syscalls in, before the first ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("clone")
        }
        org.jetbrains.compose.web.dom.Text(" — so there is a second problem behind the first. The same switch on the glibc build of the same program runs clean, which is how you know the switch is not the cause.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("the-prediction-that-was-wrong") }) {
        org.jetbrains.compose.web.dom.Text("The prediction that was wrong")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The research brief behind this work predicted that even if the link succeeded, DNS would fail. The reasoning was correct and out of date: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("getaddrinfo")
        }
        org.jetbrains.compose.web.dom.Text(" under a static glibc used to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("dlopen")
        }
        org.jetbrains.compose.web.dom.Text(" the NSS modules, and an image with nothing in it has none. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Since glibc 2.34, ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("nss_files")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("nss_dns")
            }
            org.jetbrains.compose.web.dom.Text(" are compiled into libc")
        }
        org.jetbrains.compose.web.dom.Text(", so a static binary resolves names with nothing on disk to help it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is also the second reason the sysroot the compiler ships is the problem rather than the answer. It is glibc ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("2.19")
        }
        org.jetbrains.compose.web.dom.Text(". Even with both link-line obstructions fixed, that route would lose name resolution — it is old on the far side of the line that makes any of this work.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The claim is controlled, because \"it printed ok\" is not a measurement. The same image run with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--network none")
        }
        org.jetbrains.compose.web.dom.Text(" answers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("dns-lookup=FAIL(rc=-3 Temporary failure in name resolution)")
        }
        org.jetbrains.compose.web.dom.Text(", and a name that does not exist answers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FAIL(rc=-2 Name or service not known)")
        }
        org.jetbrains.compose.web.dom.Text(", while the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/etc/hosts")
        }
        org.jetbrains.compose.web.dom.Text(" lookup stays ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ok")
        }
        org.jetbrains.compose.web.dom.Text(" in both. Which is worth knowing for another reason: a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("scratch")
        }
        org.jetbrains.compose.web.dom.Text(" container is not config-free. Docker and Kubernetes mount ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/etc/hosts")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/etc/resolv.conf")
        }
        org.jetbrains.compose.web.dom.Text(" into every container whatever the image holds, and that is what the lookups use.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("2-the-recipe-on-a-stock-2-4-10") }) {
        org.jetbrains.compose.web.dom.Text("2. The recipe, on a stock 2.4.10")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("No patched compiler needed. Two obstructions have workarounds; the third does not apply once you are linking statically.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""linuxX64 {
    binaries.executable {
        linkerOpts("-static", "--no-dynamic-linker", "-L/usr/lib/x86_64-linux-gnu")
        freeCompilerArgs.addAll(
            "-Xoverride-konan-properties=" +
                "targetSysRoot.linux_x64=/;" +
                "crtFilesLocation.linux_x64=usr/lib/x86_64-linux-gnu;" +
                "libGcc.linux_x64=usr/lib/gcc/x86_64-linux-gnu/13;" +
                "linkerGccFlags=-lgcc -lgcc_eh -lc;" +
                "linkerKonanFlags.linux_x64=-Bstatic -lstdc++ -ldl -lm -lpthread " +
                "--defsym __cxa_demangle=Konan_cxa_demangle --gc-sections",
        )
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Five properties rather than one, and every one of them earns its place:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("targetSysRoot")
            }
            org.jetbrains.compose.web.dom.Text(" points at the host instead of the bundled glibc 2.19, which is the whole trick;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("crtFilesLocation")
            }
            org.jetbrains.compose.web.dom.Text(" and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("libGcc")
            }
            org.jetbrains.compose.web.dom.Text(" still aim into the toolchain otherwise — ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("libGcc")
            }
            org.jetbrains.compose.web.dom.Text(" is documented as sysroot-relative, so it is its own key rather than a subdirectory of the first;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("linkerGccFlags")
            }
            org.jetbrains.compose.web.dom.Text(" loses ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-lgcc_s")
            }
            org.jetbrains.compose.web.dom.Text(", which has no static archive anywhere;")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("linkerKonanFlags")
            }
            org.jetbrains.compose.web.dom.Text(" is ")
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("the stock value with ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("-Bdynamic")
                }
                org.jetbrains.compose.web.dom.Text(" removed and nothing else changed")
            }
            org.jetbrains.compose.web.dom.Text(". The ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--defsym __cxa_demangle=Konan_cxa_demangle --gc-sections")
            }
            org.jetbrains.compose.web.dom.Text(" at the end is its stock tail, copied as is — the ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--defsym")
            }
            org.jetbrains.compose.web.dom.Text(" wires the runtime's own demangler into the linker, and ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--gc-sections")
            }
            org.jetbrains.compose.web.dom.Text(" drops unreferenced sections. The first draft of this recipe rewrote the key from scratch and so quietly lost ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--gc-sections")
            }
            org.jetbrains.compose.web.dom.Text(" too; that cost 224 432 bytes of binary for nothing. Print the key before you override it: ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("grep linkerKonanFlags.linux_x64 ~/.konan/kotlin-native-prebuilt-*/konan/konan.properties")
            }
            org.jetbrains.compose.web.dom.Text(", and note that its value continues onto a second line.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And two flags that are not properties. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--no-dynamic-linker")
        }
        org.jetbrains.compose.web.dom.Text(" is what removes the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PT_INTERP")
        }
        org.jetbrains.compose.web.dom.Text(" that ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-static")
        }
        org.jetbrains.compose.web.dom.Text(" alone leaves behind — omit it and the kernel hands your statically linked program to glibc's dynamic loader, which relocates it as though it were dynamic and segfaults with no output. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-L/usr/lib/x86_64-linux-gnu")
        }
        org.jetbrains.compose.web.dom.Text(" is needed because with the sysroot at ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/")
        }
        org.jetbrains.compose.web.dom.Text(" the compiler emits ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-L/lib")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-L/usr/lib")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-L/lib64")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-L/usr/lib64")
        }
        org.jetbrains.compose.web.dom.Text(", which is the layout of ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("its")
        }
        org.jetbrains.compose.web.dom.Text(" sysroot; on a multiarch distribution every archive is somewhere else, and without this the link fails with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("unable to find library -lc")
        }
        org.jetbrains.compose.web.dom.Text(" rather than with anything about static linking.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("You need ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libc6-dev")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libstdc++-dev")
        }
        org.jetbrains.compose.web.dom.Text(" on the build machine. The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("13")
        }
        org.jetbrains.compose.web.dom.Text(" in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libGcc")
        }
        org.jetbrains.compose.web.dom.Text(" is the gcc version — check ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ls /usr/lib/gcc/x86_64-linux-gnu/")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("With the patch of ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-89362") {
            org.jetbrains.compose.web.dom.Text("KT-89362")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--no-dynamic-linker")
        }
        org.jetbrains.compose.web.dom.Text(" and the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linkerKonanFlags")
        }
        org.jetbrains.compose.web.dom.Text(" override both go away and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-static")
        }
        org.jetbrains.compose.web.dom.Text(" means static. That is measured, not assumed: a distribution was built from the same tree twice, with and without the three lines, and the probe compiled with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-linker-option -static")
        }
        org.jetbrains.compose.web.dom.Text(" and nothing else gives ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PT_INTERP=1")
        }
        org.jetbrains.compose.web.dom.Text(" and exit 139 without them, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PT_INTERP=0")
        }
        org.jetbrains.compose.web.dom.Text(" and a clean run with them.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A caveat that belongs next to the recipe rather than at the end.")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xoverride-konan-properties")
        }
        org.jetbrains.compose.web.dom.Text(" pins five ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("konan.properties")
        }
        org.jetbrains.compose.web.dom.Text(" keys, and they are not a stable interface. Sergey Bogolepov of JetBrains, on ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-38876") {
            org.jetbrains.compose.web.dom.Text("KT-38876")
        }
        org.jetbrains.compose.web.dom.Text(" in March 2021:")
    }
    com.varabyte.kobweb.compose.dom.GenericTag("blockquote") {
        org.jetbrains.compose.web.dom.P {
            org.jetbrains.compose.web.dom.Text("Contents of ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("konan.properties")
            }
            org.jetbrains.compose.web.dom.Text(" (located in ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("${'$'}HOME/.konan/kotlin-native-.../konan")
            }
            org.jetbrains.compose.web.dom.Text(") may change in every release (even in a patch release), so be sure to check it before using ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-Xoverride-konan-properties")
            }
            org.jetbrains.compose.web.dom.Text(".")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So this is a recipe to use knowingly in something you build and ship, checking the keys on every Kotlin bump — not to put in a shared convention plugin where it will break in someone else's service without anyone touching it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("3-which-base-image-each-variant-starts-in") }) {
        org.jetbrains.compose.web.dom.Text("3. Which base image each variant starts in")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The probe, built four ways, run in four images. Sizes are pull bytes.")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("base image")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("as the toolchain links it")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("-Wl,--as-needed")
                    }
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("--as-needed")
                    }
                    org.jetbrains.compose.web.dom.Text(" + ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("linkerGccFlags")
                    }
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("static")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("distroless/cc-debian13")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("libcrypt.so.1")
                    }
                    org.jetbrains.compose.web.dom.Text(" missing")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("runs, 10.8 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("runs, 10.8 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("runs")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("distroless/base-debian13")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("libcrypt.so.1")
                    }
                    org.jetbrains.compose.web.dom.Text(" missing")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("libgcc_s.so.1")
                    }
                    org.jetbrains.compose.web.dom.Text(" missing")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("runs, 9.6 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("runs")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("distroless/static-debian13")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("exec: no such file")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("exec: no such file")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("exec: no such file")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("runs, 1.4 MB")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("scratch")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("exec: no such file")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("exec: no such file")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("exec: no such file")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("runs, 593 KB")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two things in that table are worth reading twice.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The workaround that circulates in both tickets is ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--as-needed")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("together with")
        }
        org.jetbrains.compose.web.dom.Text(" ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-Xoverride-konan-properties=linkerGccFlags=-lgcc -lgcc_eh -lc")
        }
        org.jetbrains.compose.web.dom.Text(", passed around as one cure. They reach different lists. The property removes exactly one library — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libgcc_s")
        }
        org.jetbrains.compose.web.dom.Text(", the one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("linkerGccFlags")
        }
        org.jetbrains.compose.web.dom.Text(" contributes — and leaves ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("libcrypt")
        }
        org.jetbrains.compose.web.dom.Text(" in place; only ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--as-needed")
        }
        org.jetbrains.compose.web.dom.Text(" removes the six from the manifest. So the half that does nothing for the bug this is usually filed under is the half that gets you one image smaller, from ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("cc")
        }
        org.jetbrains.compose.web.dom.Text(" to ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("base")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("And ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("exec: no such file or directory")
        }
        org.jetbrains.compose.web.dom.Text(" in the bottom two rows is the loader, not the binary. The binary is there; there is no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("/lib64/ld-linux-x86-64.so.2")
        }
        org.jetbrains.compose.web.dom.Text(" to run it. That message is obstruction two as the kernel reports it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("4-what-it-costs-on-a-service-rather-than-a-hello-world") }) {
        org.jetbrains.compose.web.dom.Text("4. What it costs, on a service rather than a hello-world")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("katcher")
        }
        org.jetbrains.compose.web.dom.Text(" is a crash-report service: Ktor CIO, sqlx4k over SQLite, kotlinx.serialization, KSP, schema migrations at start. Nothing in its sources changed — the build gained the block from section 2 and that is all.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Read what follows as the cost of the ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("link")
        }
        org.jetbrains.compose.web.dom.Text(", measured on a real binary rather than a toy one. The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("5 383 000")
        }
        org.jetbrains.compose.web.dom.Text("-byte image below is the bare ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("scratch")
        }
        org.jetbrains.compose.web.dom.Text(" one, which serves static files and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("401")
        }
        org.jetbrains.compose.web.dom.Text("s and then fails on the first rendered page; the image you would actually ship is the one in section 5, at 9 522 896. Everything else here — the binary, the sections, RSS, p95, startup — is unaffected by that, because none of it goes through a charset.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The risk worth naming first was sqlx4k")
        }
        org.jetbrains.compose.web.dom.Text(", a Rust staticlib: a static link can demand static versions of whatever it pulls in, and OpenSSL is the usual casualty. It did not. Zero missing archives, zero undefined symbols, no ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-lssl")
        }
        org.jetbrains.compose.web.dom.Text(" or ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-lcrypto")
        }
        org.jetbrains.compose.web.dom.Text(" anywhere.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("size") }) {
        org.jetbrains.compose.web.dom.Text("Size")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("dynamic")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("static")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("binary, unstripped")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("15 575 200")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("16 518 960")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("binary, stripped")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("11 488 328")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("12 381 640")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("what ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("strip")
                    }
                    org.jetbrains.compose.web.dom.Text(" takes off")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("26 %")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("25 %")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("base image, to pull")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("10 643 700")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("whole image, to pull")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("15 542 026")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("5 383 000")
                    }
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Static linking makes the binary bigger, not smaller")
        }
        org.jetbrains.compose.web.dom.Text(" — by 893 312 bytes stripped, which is the line worth putting in your notes. Where it goes: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("+794 480")
        }
        org.jetbrains.compose.web.dom.Text(" into ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".text")
        }
        org.jetbrains.compose.web.dom.Text(" as glibc, libstdc++ and libgcc come inside, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("+134 880")
        }
        org.jetbrains.compose.web.dom.Text(" into ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".rodata")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("+34 264")
        }
        org.jetbrains.compose.web.dom.Text(" into ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".eh_frame")
        }
        org.jetbrains.compose.web.dom.Text(", minus 93 641 of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".dynsym")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".dynstr")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text(".gnu.hash")
        }
        org.jetbrains.compose.web.dom.Text(" that a static binary no longer needs. What collapses is not the binary, it is everything else: the 10 643 700 bytes of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("distroless/cc")
        }
        org.jetbrains.compose.web.dom.Text(" are simply gone.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("strip")
        }
        org.jetbrains.compose.web.dom.Text(" would take another quarter off either build. It also takes every ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("kfun:")
        }
        org.jetbrains.compose.web.dom.Text(" name out of the symbol table, and a Kotlin/Native stack trace is nothing but those names, so that is a trade against your crash reports rather than free.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("rss-and-latency") }) {
        org.jetbrains.compose.web.dom.Text("RSS and latency")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two sessions of three alternating rounds each, 1500 requests per round, same route, same harness. The second session ran while the machine was busy, which is why its numbers are both larger and wider — and why it is quoted rather than dropped:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("dynamic")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("static")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("median, quiet box")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.369–0.412 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.364–0.376 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("median, busy box")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.419–0.532 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.414–0.498 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("p95, quiet box")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.492–0.503 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.462–0.470 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("p95, busy box")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.610–0.892 ms")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.571–0.728 ms")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("RSS after start")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("34.7–36.5 MB")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("32.5–35.0 MB")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("p95 is lower for the static build in all six rounds")
        }
        org.jetbrains.compose.web.dom.Text(", by 5–7 % on the quiet box and more on the busy one. ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("RSS after start is lower in five of the six")
        }
        org.jetbrains.compose.web.dom.Text(" — the one exception is a busy-box round where it came out 0.3 MB higher — which is the dynamic loader and the shared-library mappings not being there, not a large effect. The medians overlap, so the honest reading is \"no penalty, with a small edge at the tail\", not a speed-up.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The harness is a Python client on the same host and its absolute numbers say more about itself than about the service; and the route is a static resource, because every path that touches the database is behind authentication, so this measures the HTTP path and not sqlx4k.")
    }
    org.jetbrains.compose.web.dom.H3(attrs = { id("cold-start-on-a-small-node") }) {
        org.jetbrains.compose.web.dom.Text("Cold start on a small node")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A 2-core, 5.9 GiB VPS with k0s running beside the test, image cache emptied before every round:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th()
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("dynamic on ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("cc")
                    }
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("static in ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("scratch")
                    }
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("unpack")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("1.20–1.26 s")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.B {
                        org.jetbrains.compose.web.dom.Text("0.24–0.29 s")
                    }
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("docker run")
                    }
                    org.jetbrains.compose.web.dom.Text(" → first HTTP response")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.54–0.58 s")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("0.36–0.47 s")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Both answer ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("401")
        }
        org.jetbrains.compose.web.dom.Text(". The transfer term is ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("derived rather than measured")
        }
        org.jetbrains.compose.web.dom.Text(": the node is behind NAT from the build host, and staging a real registry pull would have meant publishing the images, so it is bytes over the node's own measured bandwidth — which was noisy enough (2.37–6.36 MB/s) to be a range, 2.3–6.3 s against 0.8–2.2 s. Added up, ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("4.1–8.1 s against 1.5–2.8 s")
        }
        org.jetbrains.compose.web.dom.Text("; call it 2.8×, and note that the byte ratio behind it does not depend on the bandwidth at all.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("One thing that was not being looked for: the binary was linked against glibc 2.39 on Ubuntu 24.04 and runs on a different distribution entirely. The rule that a build image and a runtime image have to be paired by glibc version stops applying when there is no runtime image to pair with.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("5-where-this-does-not-go") }) {
        org.jetbrains.compose.web.dom.Text("5. Where this does not go")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("linuxX64")
                    }
                    org.jetbrains.compose.web.dom.Text(" only.")
                }
                org.jetbrains.compose.web.dom.Text(" ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("linuxArm64")
                }
                org.jetbrains.compose.web.dom.Text(" was never attempted. Nothing here is architecture-specific in principle, but \"in principle\" is not a measurement.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("dlopen")
                    }
                    org.jetbrains.compose.web.dom.Text(" still happens, and it is what takes you out of ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("scratch")
                    }
                    org.jetbrains.compose.web.dom.Text(".")
                }
                org.jetbrains.compose.web.dom.Text(" Name resolution survives because ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("nss_files")
                }
                org.jetbrains.compose.web.dom.Text(" and ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("nss_dns")
                }
                org.jetbrains.compose.web.dom.Text(" are inside libc since 2.34. Character sets do not: glibc's ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("iconv")
                }
                org.jetbrains.compose.web.dom.Text(" has ")
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("no")
                }
                org.jetbrains.compose.web.dom.Text(" built-in converters, and every one of them, UTF-8 included, is a gconv module ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("dlopen")
                }
                org.jetbrains.compose.web.dom.Text("ed at run time. Ktor's charset layer on Kotlin/Native is that ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("iconv")
                }
                org.jetbrains.compose.web.dom.Text(" — ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("nm")
                }
                org.jetbrains.compose.web.dom.Text(" on the binary finds ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("iconv_open")
                }
                org.jetbrains.compose.web.dom.Text(" next to ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("kfun:io.ktor.utils.io.charsets.CharsetIconv.<init>")
                }
                org.jetbrains.compose.web.dom.Text(", and ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("strings")
                }
                org.jetbrains.compose.web.dom.Text(" finds ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("/usr/lib/x86_64-linux-gnu/gconv")
                }
                org.jetbrains.compose.web.dom.Text(" and ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("GCONV_PATH")
                }
                org.jetbrains.compose.web.dom.Text(". In a bare ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("scratch")
                }
                org.jetbrains.compose.web.dom.Text(" image the first string that goes through a charset throws ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("Failed to open iconv for charset UTF-8 with error code 22")
                }
                org.jetbrains.compose.web.dom.Text(". The linker warns about none of it, and neither does anything else for a long time: the service migrates, serves static files, answers ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("401")
                }
                org.jetbrains.compose.web.dom.Text(", and renders a whole page of HTML — a ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("GET")
                }
                org.jetbrains.compose.web.dom.Text(" of an empty list is fine. It is the route that URL-encodes a name that dies. A smoke test can walk through a surprising amount of an app before reaching this.")
            }
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.Text("The fix is to put back what ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("dlopen")
                }
                org.jetbrains.compose.web.dom.Text(" needs. Not guessed — read off ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("strace -e trace=openat")
                }
                org.jetbrains.compose.web.dom.Text(" of the running binary, and copied ")
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("from the build stage")
                }
                org.jetbrains.compose.web.dom.Text(", so the shared glibc is the same build as the ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("libc.a")
                }
                org.jetbrains.compose.web.dom.Text(" the binary was linked against:")
            }
            org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-dockerfile") }) { org.jetbrains.compose.web.dom.Text("""COPY --from=build /etc/ld.so.cache /etc/ld.so.cache
COPY --from=build /lib/x86_64-linux-gnu/ld-linux-x86-64.so.2 /lib/x86_64-linux-gnu/
COPY --from=build /lib/x86_64-linux-gnu/libc.so.6 /lib/x86_64-linux-gnu/
COPY --from=build /usr/lib/x86_64-linux-gnu/gconv /usr/lib/x86_64-linux-gnu/gconv
""") } }
            org.jetbrains.compose.web.dom.Table {
                org.jetbrains.compose.web.dom.Thead {
                    org.jetbrains.compose.web.dom.Tr {
                        org.jetbrains.compose.web.dom.Th {
                            org.jetbrains.compose.web.dom.Text("image")
                        }
                        org.jetbrains.compose.web.dom.Th {
                            org.jetbrains.compose.web.dom.Text("to pull")
                        }
                        org.jetbrains.compose.web.dom.Th {
                            org.jetbrains.compose.web.dom.Text("vs today")
                        }
                    }
                }
                org.jetbrains.compose.web.dom.Tbody {
                    org.jetbrains.compose.web.dom.Tr {
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.Code {
                                org.jetbrains.compose.web.dom.Text("distroless/cc")
                            }
                            org.jetbrains.compose.web.dom.Text(", dynamic — today")
                        }
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.Text("15 542 820")
                        }
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.Text("—")
                        }
                    }
                    org.jetbrains.compose.web.dom.Tr {
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.Text("the four ")
                            org.jetbrains.compose.web.dom.Code {
                                org.jetbrains.compose.web.dom.Text("COPY")
                            }
                            org.jetbrains.compose.web.dom.Text(" lines above, whole gconv tree")
                        }
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.B {
                                org.jetbrains.compose.web.dom.Text("9 522 896")
                            }
                        }
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.Text("1.63×")
                        }
                    }
                    org.jetbrains.compose.web.dom.Tr {
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.Code {
                                org.jetbrains.compose.web.dom.Text("gconv-modules.cache")
                            }
                            org.jetbrains.compose.web.dom.Text(" and ")
                            org.jetbrains.compose.web.dom.Code {
                                org.jetbrains.compose.web.dom.Text("UTF-16.so")
                            }
                            org.jetbrains.compose.web.dom.Text(" only")
                        }
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.Text("6 710 489")
                        }
                        org.jetbrains.compose.web.dom.Td {
                            org.jetbrains.compose.web.dom.Text("2.32×")
                        }
                    }
                }
            }
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("Take the first one.")
                }
                org.jetbrains.compose.web.dom.Text(" The second is a curated list of modules, and curating it is a guard by shape rather than by rule: glibc chose ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("UTF-16.so")
                }
                org.jetbrains.compose.web.dom.Text(" to convert ")
                org.jetbrains.compose.web.dom.Em {
                    org.jetbrains.compose.web.dom.Text("UTF-8")
                }
                org.jetbrains.compose.web.dom.Text(", which nobody would have predicted, and two traces of the same binary disagree about whether that module is opened at all — a ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("POST")
                }
                org.jetbrains.compose.web.dom.Text(" that URL-encodes a name loads it, a ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("GET")
                }
                org.jetbrains.compose.web.dom.Text(" of the same page stops at ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("gconv-modules.cache")
                }
                org.jetbrains.compose.web.dom.Text(". A module list read off a trace covers the paths the trace walked, and the failure for a path it did not walk arrives in production. 2.8 MB is not worth that.")
            }
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.Text("Note what the image is now: a static binary next to a dynamic loader and a shared libc, so that ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("dlopen")
                }
                org.jetbrains.compose.web.dom.Text(" has something to open. That is not ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("FROM scratch")
                }
                org.jetbrains.compose.web.dom.Text(" in any honest sense, and the one thing it does keep from this whole exercise is the pairing rule going away — the runtime glibc comes out of the builder, so it cannot be the wrong version.")
            }
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.Text("This was found the expensive way: the smoke test was ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("GET /")
                }
                org.jetbrains.compose.web.dom.Text(" answering ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("401")
                }
                org.jetbrains.compose.web.dom.Text(", which never reaches a rendered page. Exercise a route that returns real content before you believe a small-image result — and be equally careful about what you exclude. The timezone database looked like the obvious blocker here and is not one: ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("strace")
                }
                org.jetbrains.compose.web.dom.Text(" shows ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("TimeZone.currentSystemDefault()")
                }
                org.jetbrains.compose.web.dom.Text(" opening neither ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("/usr/share/zoneinfo")
                }
                org.jetbrains.compose.web.dom.Text(" nor ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("/etc/localtime")
                }
                org.jetbrains.compose.web.dom.Text(", in either image. What it returns instead was not chased down — the service pins no zone of its own, so a ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("currentSystemDefault()")
                }
                org.jetbrains.compose.web.dom.Text(" that ignores ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("TZ")
                }
                org.jetbrains.compose.web.dom.Text(" would be a kotlinx-datetime question rather than an image one, and it deserves its own hello-world rather than a sentence here.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("ASLR was already not there.")
                }
                org.jetbrains.compose.web.dom.Text(" A static non-PIE executable loses address-space randomisation of the main image — except that Kotlin/Native's Linux executables are ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("ET_EXEC")
                }
                org.jetbrains.compose.web.dom.Text(" to begin with. ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("readelf -h")
                }
                org.jetbrains.compose.web.dom.Text(" says ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("Type: EXEC (Executable file)")
                }
                org.jetbrains.compose.web.dom.Text(" for ")
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("both")
                }
                org.jetbrains.compose.web.dom.Text(" builds of this service, static and dynamic alike, so static linking takes nothing away here; the randomisation was never there to lose. That is worth knowing in its own right if you are hardening a service. ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("-static-pie")
                }
                org.jetbrains.compose.web.dom.Text(" was not attempted, and whether the Kotlin/Native runtime survives it is an open question rather than a known answer.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("ktor-client-curl")
                    }
                    org.jetbrains.compose.web.dom.Text(" is out of scope.")
                }
                org.jetbrains.compose.web.dom.Text(" It bundles its own statically linked OpenSSL, which is a different linking question and was not examined. ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("katcher")
                }
                org.jetbrains.compose.web.dom.Text(" uses Ktor's server with CIO.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("musl is a no, and section 1 says why.")
                }
                org.jetbrains.compose.web.dom.Text(" It is not a matter of finding the right flags: the runtime deadlocks in GC thread coordination before the first ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("println")
                }
                org.jetbrains.compose.web.dom.Text(", and there is a second failure behind that one. The route into a small image is static glibc, not musl.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("Windows and macOS are a different question.")
                }
                org.jetbrains.compose.web.dom.Text(" ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("MingwBasedLinker")
                }
                org.jetbrains.compose.web.dom.Text(" and ")
                org.jetbrains.compose.web.dom.Code {
                    org.jetbrains.compose.web.dom.Text("MacOSBasedLinker")
                }
                org.jetbrains.compose.web.dom.Text(" are separate code paths and none of this was measured against them.")
            }
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.P {
                org.jetbrains.compose.web.dom.B {
                    org.jetbrains.compose.web.dom.Text("The ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("konan.properties")
                    }
                    org.jetbrains.compose.web.dom.Text(" keys are not a stable interface")
                }
                org.jetbrains.compose.web.dom.Text(", as section 2 says. Today's recipe is five pinned keys; if ")
                com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-89362") {
                    org.jetbrains.compose.web.dom.Text("KT-89362")
                }
                org.jetbrains.compose.web.dom.Text(" lands it becomes one flag, and this article's section 2 becomes shorter.")
            }
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("who-this-is-actually-for") }) {
        org.jetbrains.compose.web.dom.Text("Who this is actually for")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two audiences, and they get different things.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A binary that never puts a string through a charset")
        }
        org.jetbrains.compose.web.dom.Text(" — a CLI, an agent, a batch job, something speaking its own protocol over raw sockets — gets the article as written: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("FROM scratch")
        }
        org.jetbrains.compose.web.dom.Text(", nothing in the image but the executable, 593 KB to pull for a hello-world.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A Ktor service")
        }
        org.jetbrains.compose.web.dom.Text(" gets the four ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("COPY")
        }
        org.jetbrains.compose.web.dom.Text(" lines from section 5 and an image with a loader and a libc in it: 9 522 896 bytes against 15 542 820, 1.63×. Less of a headline, still most of a base image gone, and the glibc-pairing rule goes with it.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-would-make-this-ordinary") }) {
        org.jetbrains.compose.web.dom.Text("What would make this ordinary")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Three lines in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("GccBasedLinker.finalLinkCommands")
        }
        org.jetbrains.compose.web.dom.Text(", skipping the interpreter when the user asked for a static link — that is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/JetBrains/kotlin/pull/8127") {
            org.jetbrains.compose.web.dom.Text("the patch")
        }
        org.jetbrains.compose.web.dom.Text(", with a test that fails without it. After that, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("-linker-option -static")
        }
        org.jetbrains.compose.web.dom.Text(" means what it says, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("--no-dynamic-linker")
        }
        org.jetbrains.compose.web.dom.Text(" stops being folklore, and the remaining overrides are about which glibc you link against rather than about undoing the toolchain.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("platform.posix")
        }
        org.jetbrains.compose.web.dom.Text(" manifest is the older and more structural of the two: six libraries added to every Linux binary, five of them dead on any modern glibc, and no setting a user can reach. That one needs a decision rather than a patch, and it is ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://youtrack.jetbrains.com/issue/KT-55643") {
            org.jetbrains.compose.web.dom.Text("KT-55643")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Reproduction, transcripts and the exact commands: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/sborka/tree/main/docs/research/static-probe") {
            org.jetbrains.compose.web.dom.Text("sborka/docs/research/static-probe")
        }
        org.jetbrains.compose.web.dom.Text(". ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("./experiments.sh")
        }
        org.jetbrains.compose.web.dom.Text(" on a Linux host with a JDK, docker and binutils produces every number above in one run; it fetches the ~1 GB Kotlin/Native toolchain on a first run and says so.")
    }
}
